import { useMutation } from "@tanstack/react-query";

import type { BillingNudgeKey } from "~/features/billing/nudges";
import { useApiClient } from "~/hooks/use-api-client";
import type {
    BillingMetricType,
    BillingTierName,
} from "~/modules.server/billing";
import { trackEvent } from "~/lib/analytics";

interface StartUpgradeCheckoutInput {
    source: string;
    /** Membership A/B arm + state, for conversion attribution. */
    paywallVariant?: string;
    paywallState?: string;
    nudgeKey?: BillingNudgeKey;
    priceId?: string;
    /** Same-origin path to return to after checkout; re-validated server-side. */
    returnTo?: string;
    /** Voice-lab experiment to apply server-side once the entitlement lands. */
    pendingVoiceExperimentId?: string;
}

interface CheckoutResponse {
    kind: "checkout";
    url: string;
    offer: {
        priceId: string;
        unitAmountCents: number;
        monthlyLimitUnits: number;
        tierName: BillingTierName;
        metricType: BillingMetricType;
    };
}

// AIDEV-NOTE: api.billing.checkout returns 409 existing_paid_subscription for accounts already
// paying; they must upgrade in place (prorated) from the membership page, not via a 2nd Checkout.
interface ExistingPaidSubscriptionRedirect {
    kind: "membership_redirect";
    redirectTo: string;
}

interface CheckoutErrorPayload {
    error?: string;
    code?: string;
    redirectTo?: string;
}

export function useUpgradeFlow() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (
            input: StartUpgradeCheckoutInput,
        ): Promise<CheckoutResponse | ExistingPaidSubscriptionRedirect> => {
            const response = await apiClient.post("/billing/checkout", {
                source: input.source,
                nudgeKey: input.nudgeKey,
                priceId: input.priceId,
                returnTo: input.returnTo,
                pendingVoiceExperimentId: input.pendingVoiceExperimentId,
            });
            const payload = (await response.json().catch(() => null)) as
                | Omit<CheckoutResponse, "kind">
                | CheckoutErrorPayload
                | null;

            if (
                response.status === 409 &&
                payload &&
                "code" in payload &&
                payload.code === "existing_paid_subscription" &&
                payload.redirectTo
            ) {
                return {
                    kind: "membership_redirect",
                    redirectTo: payload.redirectTo,
                };
            }

            if (!response.ok) {
                throw new Error(
                    payload && "error" in payload && payload.error
                        ? payload.error
                        : "Failed to start checkout",
                );
            }

            if (!payload || !("url" in payload) || !payload.url) {
                throw new Error("Checkout URL was missing from response");
            }

            return { ...payload, kind: "checkout" };
        },
        onSuccess: (payload, variables) => {
            if (payload.kind === "membership_redirect") {
                window.location.assign(payload.redirectTo);
                return;
            }

            // AIDEV-NOTE: Billing checkout start is tracked separately from generic clicks for funnel analysis.
            trackEvent("billing_checkout_started", {
                source: variables.source,
                paywallVariant: variables.paywallVariant,
                paywallState: variables.paywallState,
                nudgeKey: variables.nudgeKey,
                priceId: payload.offer.priceId,
                tierName: payload.offer.tierName,
                metricType: payload.offer.metricType,
                monthlyLimitUnits: payload.offer.monthlyLimitUnits,
                unitAmountCents: payload.offer.unitAmountCents,
            });

            window.location.assign(payload.url);
        },
    });
}
